import {
  Box,
  Button,
  Center,
  Container,
  Image,
  MantineProvider,
  Text,
  TextInput,
  Flex,
  LoadingOverlay,
  ActionIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import React, { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { IconHelpCircle } from "@tabler/icons-react";
import axios from "axios";
import { MaintenanceInfo } from "../components/MaintenanceInfo";
import { StatusDisplay } from "../components/StatusDisplay";
import { RenderStatusContents } from "../components/RenderStatusContents";
import { RenderCurrentStatusContents } from "../components/RenderCurrentStatusContents";

export const Status = () => {
  // クエリパラメータからインスタンス名を取得
  const queryParams = new URLSearchParams(useLocation().search);
  const queryParamInstance = queryParams.get("instance_name") ?? "";

  const form = useForm({
    initialValues: {
      instanceName: queryParamInstance,
    },
    validate: {
      instanceName: (value) =>
          value.length < 1
              ? "インスタンス名を入力してください。"
              : (!/^[a-z0-9_]+$/.test(value) || /^[0-9]/.test(value))
              ? "インスタンス名が不正です。"
              : null,
    },
  });

  const [instanceName, setInstanceName] = useState(queryParamInstance);
  const [statusContents, setStatusContents] = useState<ReactNode>();
  const [currentStatus, setCurrentStatus] = useState("");
  const [currentStatusContents, setCurrentStatusContents] = useState<ReactNode>();
  const [loadingVisible, showLoading] = useState(false);

  useEffect(() => {
    if (instanceName.length === 0) {
      return;
    }

    // クエリパラメーター用のインスタンス名チェック
    if (form.validate().hasErrors) {
      return;
    }

    showLoading(true);

    // ステータス情報を取得
    axios.get(`${process.env.REACT_APP_STATUS_API_ENDPOINT}/instances/${instanceName}/statuses`)
      .then(response => {
        const statuses = JSON.parse(response.data).records;
        const currentStatus = statuses[statuses.length - 1].status;
        setCurrentStatus(currentStatus);

        // 現在のステータスのコンテンツをセット
        setCurrentStatusContents(RenderCurrentStatusContents(currentStatus, instanceName));

        // 稼働情報のコンテンツをセット
        setStatusContents(RenderStatusContents(statuses));
      })
      .catch(error => {
        alert("稼働状況の取得に失敗しました。");
        console.log(error);
      })
      .finally(() =>{
        showLoading(false);
      });
  },[instanceName])

  const navigate = useNavigate();

  // クエリパラメーターにインスタンス名を付加
  const updateQueryParam = (instanceName: string) => {
    navigate("?instance_name=" + instanceName);
  };

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS>
      <Box pos="relative">
        <LoadingOverlay visible={loadingVisible} />
        <Container maw={640} mt={44}>
          <Center mb={24} display={"flex"} style={{ alignItems: "flex-end"}}>
            <Image
              src="collaboflow_logo.png"
              alt="collaboflow logo"
              maw={300}
              mr={20}
            />
            <Text fz="30px" fw={500} style={{ lineHeight: "100%" }}>サービス稼働状況</Text>
          </Center>
          <Center>
            <Box miw={540}>
              <form onSubmit={form.onSubmit((values) => {
                setInstanceName(values.instanceName);
                updateQueryParam(values.instanceName);
              })}>
                <Flex>
                  <Text fz="lg" fw={700}>インスタンス名の入力</Text>
                  <Box
                      component="a"
                      href="https://collaboflow.zendesk.com/hc/ja/articles/4425823538575"
                      target="_blank"
                      ml={8}
                  >
                      <ActionIcon size="sm" mt={3} style={{ color: "#737373" }}>
                          <IconHelpCircle />
                      </ActionIcon>
                  </Box>
                </Flex>
                <Text fz="sm" c="dimmed" mb={12}>
                  「https://cloud.collaboflow.com/｛インスタンス名｝/」の｛インスタンス名｝部分が<br />インスタンス名です。
                </Text>
                <TextInput
                  placeholder="インスタンス名"
                  mb={24}
                  maxLength={20}
                  {...form.getInputProps("instanceName")}
                />
                <Center>
                  <Button type="submit" size="md" fullWidth onClick={() => setInstanceName("")}>{currentStatus.length ? "更新" : "稼働状況を表示"}</Button>
                </Center>
              </form>
            </Box>
          </Center>

          <Box p={16} mt={32} bg={"#f3f3f3"} style={{ borderRadius: 10 }} hidden={currentStatus.length ? false : true}>
            <StatusDisplay currentStatusContents={currentStatusContents} statusContents={statusContents} />
            <MaintenanceInfo />  
          </Box>
        </Container>
      </Box>
    </MantineProvider>
  );
}
